import React, { useState } from 'react';
import { BrandRiskAnalysis } from '../../../shared/types';
import { scanApi } from '@/services/api';
import { Button } from '@/components/ui/button';
import {
  ShieldCheck, ShieldAlert, AlertTriangle, Info, AlertCircle,
  XCircle, RefreshCw, Globe, CheckCircle2
} from 'lucide-react';

interface BrandRiskAnalysisProps {
  brandRisk?: BrandRiskAnalysis;
  domain: string;
  className?: string;
  onAnalysisComplete?: (analysis: BrandRiskAnalysis) => void;
}

// ─── Score gauge (SVG arc) ────────────────────────────────────────────────────
function ScoreGauge({ score, level }: { score: number; level: 'low' | 'medium' | 'high' }) {
  const R = 36;
  const cx = 44, cy = 44;
  const startAngle = 210;
  const endAngle = startAngle + (score / 100) * 300;

  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const arcPath = (from: number, to: number, r: number) => {
    const sx = cx + r * Math.cos(toRad(from));
    const sy = cy + r * Math.sin(toRad(from));
    const ex = cx + r * Math.cos(toRad(to));
    const ey = cy + r * Math.sin(toRad(to));
    const large = to - from > 180 ? 1 : 0;
    return `M ${sx} ${sy} A ${r} ${r} 0 ${large} 1 ${ex} ${ey}`;
  };

  const trackColor = '#f3f4f6';
  const fillColor = level === 'high' ? '#ef4444' : level === 'medium' ? '#f59e0b' : '#22c55e';

  return (
    <svg width="88" height="88" viewBox="0 0 88 88" className="flex-shrink-0">
      <path d={arcPath(210, 510, R)} fill="none" stroke={trackColor} strokeWidth="7" strokeLinecap="round" />
      {score > 0 && (
        <path d={arcPath(210, endAngle, R)} fill="none" stroke={fillColor} strokeWidth="7" strokeLinecap="round" />
      )}
      <text x={cx} y={cy + 4} textAnchor="middle" className="text-sm font-bold" fontSize="15" fontWeight="700" fill="#111827">
        {score}
      </text>
      <text x={cx} y={cy + 16} textAnchor="middle" fontSize="8" fill="#9ca3af">
        / 100
      </text>
    </svg>
  );
}

// ─── Factor bar row ───────────────────────────────────────────────────────────
const FACTOR_META: Record<string, { label: string; description: string; weight: string }> = {
  brandCollisionRisk: {
    label: 'Brand Collision',
    description: 'How closely the name resembles a trademarked global brand (Levenshtein distance)',
    weight: '35%',
  },
  tldCrossRisk: {
    label: 'TLD Cross-Ownership',
    description: 'Whether .com / .net / .org equivalents are registered by someone else',
    weight: '20%',
  },
  typosquattingRisk: {
    label: 'Typosquatting Exposure',
    description: 'Number of registered keyboard-adjacent / transposition variants of this domain',
    weight: '25%',
  },
  socialMediaRisk: {
    label: 'Social Media Handles',
    description: 'Whether key social handles are already taken by third parties',
    weight: '10%',
  },
  reputationRisk: {
    label: 'Reputation Signals',
    description: 'Negative keyword patterns or known spam/phish associations in the domain name',
    weight: '10%',
  },
};

const FACTOR_ORDER = [
  'brandCollisionRisk', 'tldCrossRisk', 'typosquattingRisk', 'socialMediaRisk', 'reputationRisk',
];

function FactorBar({ factorKey, value }: { factorKey: string; value: number }) {
  const meta = FACTOR_META[factorKey] ?? { label: factorKey, description: '', weight: '' };
  const barColor = value >= 65 ? 'bg-red-400' : value >= 35 ? 'bg-amber-400' : 'bg-green-400';

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <span className="text-sm text-gray-700">{meta.label}</span>
          {meta.weight && (
            <span className="text-xs text-gray-400">({meta.weight})</span>
          )}
        </div>
        <span className="text-sm font-medium text-gray-900 tabular-nums">{Math.round(value)}</span>
      </div>
      <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-500 ${barColor}`} style={{ width: `${value}%` }} />
      </div>
      {meta.description && (
        <p className="text-xs text-gray-400 leading-tight">{meta.description}</p>
      )}
    </div>
  );
}

// ─── Finding item ─────────────────────────────────────────────────────────────
const SEVERITY_STYLE = {
  critical: { icon: XCircle,      text: 'text-red-500',   bg: 'bg-red-50 border-red-100',   badge: 'bg-red-100 text-red-700'   },
  warning:  { icon: AlertCircle,  text: 'text-amber-500', bg: 'bg-amber-50 border-amber-100',badge: 'bg-amber-100 text-amber-700' },
  info:     { icon: Info,         text: 'text-blue-400',  bg: 'bg-gray-50 border-gray-100',  badge: 'bg-gray-100 text-gray-600'  },
};

function FindingItem({ detail }: { detail: BrandRiskAnalysis['details'][0] }) {
  const s = SEVERITY_STYLE[detail.severity] ?? SEVERITY_STYLE.info;
  const Icon = s.icon;
  return (
    <div className={`flex items-start gap-3 px-3 py-2.5 rounded-lg border ${s.bg}`}>
      <Icon className={`h-3.5 w-3.5 mt-0.5 flex-shrink-0 ${s.text}`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-800 leading-snug">{detail.message}</p>
        {detail.actionable && (
          <p className="text-xs text-gray-500 mt-0.5 leading-snug">{detail.actionable}</p>
        )}
      </div>
    </div>
  );
}

// ─── Category group (collapsible) ─────────────────────────────────────────────
function CategoryGroup({ category, findings }: {
  category: string;
  findings: BrandRiskAnalysis['details'];
}) {
  const [open, setOpen] = useState(true);
  const criticalCount = findings.filter(f => f.severity === 'critical').length;
  const warningCount  = findings.filter(f => f.severity === 'warning').length;

  return (
    <div>
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center justify-between w-full py-2 text-left group"
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">{category}</span>
          <div className="flex gap-1">
            {criticalCount > 0 && (
              <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-medium">
                {criticalCount} critical
              </span>
            )}
            {warningCount > 0 && (
              <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium">
                {warningCount} warning
              </span>
            )}
          </div>
        </div>
        <span className="text-gray-400 text-xs">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="space-y-1.5 pb-2">
          {findings.map((f, i) => <FindingItem key={i} detail={f} />)}
        </div>
      )}
    </div>
  );
}

// ─── Conflicting brands chips ─────────────────────────────────────────────────
function ConflictingBrands({ brands }: { brands: NonNullable<BrandRiskAnalysis['conflictingBrands']> }) {
  return (
    <div className="bg-gray-50 border border-gray-100 rounded-lg p-4">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2.5">Brand Conflicts</p>
      <div className="flex flex-wrap gap-1.5">
        {brands.map((b, i) => (
          <span
            key={i}
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
              b.severity === 'critical' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
            }`}
          >
            {b.brand}
            <span className="opacity-60">d={b.distance}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

// ─── TLD ownership grid ───────────────────────────────────────────────────────
function TldOwnershipGrid({ ownership }: { ownership: NonNullable<BrandRiskAnalysis['tldOwnership']> }) {
  return (
    <div className="bg-gray-50 border border-gray-100 rounded-lg p-4">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2.5 flex items-center gap-1.5">
        <Globe className="h-3 w-3" /> TLD Ownership
      </p>
      <div className="flex flex-wrap gap-2">
        {ownership.map((t, i) => (
          <div key={i} className="flex items-center gap-1.5">
            {t.registered
              ? <XCircle className="h-3.5 w-3.5 text-red-400 flex-shrink-0" />
              : <CheckCircle2 className="h-3.5 w-3.5 text-green-400 flex-shrink-0" />
            }
            <span className={`text-xs font-mono font-medium ${t.registered ? 'text-red-600' : 'text-gray-500'}`}>
              .{t.tld}
            </span>
          </div>
        ))}
      </div>
      <p className="text-xs text-gray-400 mt-2">
        <XCircle className="h-3 w-3 inline mr-1 text-red-400" />registered &nbsp;
        <CheckCircle2 className="h-3 w-3 inline mr-1 text-green-400" />available
      </p>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
const BrandRiskAnalysisComponent: React.FC<BrandRiskAnalysisProps> = ({
  brandRisk,
  domain,
  className = '',
  onAnalysisComplete,
}) => {
  const [reanalyzing, setReanalyzing] = useState(false);
  const [reanalysisStep, setReanalysisStep] = useState('');
  const [reanalysisProgress, setReanalysisProgress] = useState(0);
  const [reanalysisError, setReanalysisError] = useState<string | null>(null);

  const handleReanalyze = async () => {
    setReanalyzing(true);
    setReanalysisError(null);
    setReanalysisProgress(0);
    try {
      const result = await scanApi.analyzeBrandRisk(
        domain,
        (step, progress) => { setReanalysisStep(step); setReanalysisProgress(progress); },
        (analysis) => { onAnalysisComplete?.(analysis); },
        (err) => { setReanalysisError(err); }
      );
      if (result) onAnalysisComplete?.(result);
    } catch (err) {
      setReanalysisError(err instanceof Error ? err.message : 'Re-analysis failed');
    } finally {
      setReanalyzing(false);
    }
  };

  if (!brandRisk) return null;

  const { riskLevel, riskScore, confidence, factors, details, recommendations } = brandRisk;

  // Risk level display config
  const levelConfig = {
    low:    { label: 'Low Risk',    Icon: ShieldCheck,  color: 'text-green-600', badge: 'bg-green-50 text-green-700 border-green-200' },
    medium: { label: 'Medium Risk', Icon: ShieldAlert,  color: 'text-amber-600', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
    high:   { label: 'High Risk',   Icon: AlertTriangle,color: 'text-red-600',   badge: 'bg-red-50 text-red-700 border-red-200' },
  }[riskLevel];

  const confConfig = {
    low:    { label: 'Low confidence',    badge: 'bg-gray-100 text-gray-500' },
    medium: { label: 'Medium confidence', badge: 'bg-blue-50 text-blue-600' },
    high:   { label: 'High confidence',   badge: 'bg-green-50 text-green-700' },
  }[confidence];

  // Group details by category
  const grouped = details.reduce<Record<string, BrandRiskAnalysis['details']>>((acc, d) => {
    (acc[d.category] ??= []).push(d);
    return acc;
  }, {});

  // Category priority order
  const CAT_ORDER = ['Brand Conflict', 'Homograph', 'TLD Conflict', 'Typosquatting', 'Social Media', 'Reputation'];
  const sortedCategories = [
    ...CAT_ORDER.filter(c => grouped[c]),
    ...Object.keys(grouped).filter(c => !CAT_ORDER.includes(c)),
  ];

  // Only show factors that exist and have a non-trivial value or definition
  const factorEntries = FACTOR_ORDER
    .filter(k => k in factors)
    .map(k => [k, (factors as Record<string, number>)[k]] as [string, number]);

  return (
    <div className={`space-y-6 ${className}`}>

      {/* ── Score header ── */}
      <div className="flex items-center gap-5">
        <ScoreGauge score={riskScore} level={riskLevel} />
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1.5 text-sm font-semibold px-2.5 py-1 rounded-full border ${levelConfig.badge}`}>
              <levelConfig.Icon className="h-3.5 w-3.5" />
              {levelConfig.label}
            </span>
            <span className={`text-xs px-2 py-1 rounded-full ${confConfig.badge}`}>
              {confConfig.label}
            </span>
          </div>
          <p className="text-sm text-gray-500 mt-1.5 leading-snug">
            {riskLevel === 'low'
              ? 'No significant brand conflicts detected. Proceed with standard protection steps.'
              : riskLevel === 'medium'
              ? 'Some brand concerns found. Verify trademark status before committing.'
              : 'Significant brand conflicts detected. Legal review strongly recommended.'}
          </p>
        </div>
      </div>

      {/* ── Brand conflicts + TLD ownership ── */}
      {((brandRisk.conflictingBrands?.length ?? 0) > 0 || (brandRisk.tldOwnership?.length ?? 0) > 0) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {(brandRisk.conflictingBrands?.length ?? 0) > 0 && (
            <ConflictingBrands brands={brandRisk.conflictingBrands!} />
          )}
          {(brandRisk.tldOwnership?.length ?? 0) > 0 && (
            <TldOwnershipGrid ownership={brandRisk.tldOwnership!} />
          )}
        </div>
      )}

      {/* ── Risk factor bars ── */}
      {factorEntries.length > 0 && (
        <div className="bg-gray-50 border border-gray-100 rounded-lg p-4 space-y-4">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Risk Factors</p>
          {factorEntries.map(([k, v]) => (
            <FactorBar key={k} factorKey={k} value={v} />
          ))}
        </div>
      )}

      {/* ── Findings by category ── */}
      {sortedCategories.length > 0 && (
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">Findings</p>
          <div className="divide-y divide-gray-100">
            {sortedCategories.map(cat => (
              <CategoryGroup key={cat} category={cat} findings={grouped[cat]} />
            ))}
          </div>
        </div>
      )}

      {/* ── Recommendations ── */}
      {recommendations.length > 0 && (
        <div className="bg-gray-50 border border-gray-100 rounded-lg p-4">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">Recommendations</p>
          <ul className="space-y-2">
            {recommendations.map((rec, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-gray-400 flex-shrink-0" />
                <span className="text-sm text-gray-700 leading-snug">{rec}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ── Re-analyze ── */}
      <div className="pt-2">
        {reanalyzing ? (
          <div className="space-y-2">
            <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-400 rounded-full transition-all duration-500"
                style={{ width: `${reanalysisProgress}%` }}
              />
            </div>
            <p className="text-xs text-gray-500 text-center">{reanalysisStep || 'Analyzing…'}</p>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <Button
              onClick={handleReanalyze}
              variant="outline"
              size="sm"
              className="text-gray-600 border-gray-200 hover:bg-gray-50"
            >
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
              Re-analyze
            </Button>
            {reanalysisError && (
              <p className="text-xs text-red-500">{reanalysisError}</p>
            )}
            <p className="text-xs text-gray-400 ml-auto">
              Analysis may take 5–10 seconds (live DNS checks)
            </p>
          </div>
        )}
      </div>

      {/* ── Disclaimer ── */}
      <p className="text-xs text-gray-400 border-t border-gray-100 pt-4">
        Automated analysis only — not a substitute for trademark search or legal counsel.
      </p>
    </div>
  );
};

export default BrandRiskAnalysisComponent;
